// Глобус «Рейсы в пути»: математика без DOM (ортографическая проекция, дуги большого круга).
export const DEG = Math.PI / 180

// Точки суши: base64 от Int16 (little-endian) пар [долгота×10, широта×10].
export function decodeDots(b64) {
  const bin = atob(b64)
  const view = new DataView(new ArrayBuffer(bin.length))
  for (let i = 0; i < bin.length; i += 1) view.setUint8(i, bin.charCodeAt(i))
  const count = bin.length / 4
  const lon = new Float32Array(count)
  const sinLat = new Float32Array(count)
  const cosLat = new Float32Array(count)
  for (let i = 0; i < count; i += 1) {
    lon[i] = (view.getInt16(i * 4, true) / 10) * DEG
    const lat = (view.getInt16(i * 4 + 2, true) / 10) * DEG
    sinLat[i] = Math.sin(lat)
    cosLat[i] = Math.cos(lat)
  }
  return { count, lon, sinLat, cosLat }
}

// Вид: центр глобуса (долгота, широта в градусах). project → {x, y, z}; z > 0 — сторона к зрителю.
export function makeView(lon0, lat0) {
  const l0 = lon0 * DEG
  const sin0 = Math.sin(lat0 * DEG)
  const cos0 = Math.cos(lat0 * DEG)
  return {
    l0,
    sin0,
    cos0,
    project(lon, lat, alt = 0) {
      const la = lat * DEG
      const d = lon * DEG - l0
      const cl = Math.cos(la)
      const k = 1 + alt
      return {
        x: cl * Math.sin(d) * k,
        y: (cos0 * Math.sin(la) - sin0 * cl * Math.cos(d)) * k,
        z: sin0 * Math.sin(la) + cos0 * cl * Math.cos(d),
      }
    },
  }
}

export function toVec(lon, lat) {
  const la = lat * DEG
  const lo = lon * DEG
  return [Math.cos(la) * Math.cos(lo), Math.cos(la) * Math.sin(lo), Math.sin(la)]
}

export function toLonLat(v) {
  const n = Math.hypot(v[0], v[1], v[2]) || 1
  return { lon: Math.atan2(v[1], v[0]) / DEG, lat: Math.asin(v[2] / n) / DEG }
}

// Угол между точками (радианы).
export function angle(a, b) {
  const va = toVec(a.lon, a.lat)
  const vb = toVec(b.lon, b.lat)
  const dot = Math.max(-1, Math.min(1, va[0] * vb[0] + va[1] * vb[1] + va[2] * vb[2]))
  return Math.acos(dot)
}

// Точки дуги большого круга с «подъёмом» над поверхностью: [{lon, lat, alt}].
export function arcPoints(from, to, steps = 40) {
  const a = toVec(from.lon, from.lat)
  const b = toVec(to.lon, to.lat)
  const omega = angle(from, to)
  const height = Math.min(0.32, 0.06 + omega * 0.55)
  const out = []
  for (let i = 0; i <= steps; i += 1) {
    const t = i / steps
    let v
    if (omega < 1e-6) v = a
    else {
      const s = Math.sin(omega)
      const ka = Math.sin((1 - t) * omega) / s
      const kb = Math.sin(t * omega) / s
      v = [a[0] * ka + b[0] * kb, a[1] * ka + b[1] * kb, a[2] * ka + b[2] * kb]
    }
    out.push({ ...toLonLat(v), alt: height * Math.sin(Math.PI * t) })
  }
  return out
}

// Видна ли точка на высоте alt (над краем диска видно чуть дальше горизонта).
export function isVisible(z, alt = 0) {
  return z > -Math.sqrt(Math.max(0, (1 + alt) ** 2 - 1)) / (1 + alt) * 0.95
}

// Центр, к которому поворачивать глобус: средняя точка всех городов.
export function centroid(points, fallback = { lon: 55, lat: 52 }) {
  if (!points || !points.length) return fallback
  let x = 0
  let y = 0
  let z = 0
  for (const p of points) {
    const v = toVec(p.lon, p.lat)
    x += v[0]; y += v[1]; z += v[2]
  }
  return toLonLat([x, y, z])
}

// Кратчайшая разница долгот в градусах (−180…180].
export function lonDelta(a, b) {
  let d = (a - b) % 360
  if (d > 180) d -= 360
  if (d <= -180) d += 360
  return d
}

// Скорость вращения: у рейсов — медленно, на обратной стороне — быстрее (рейсы чаще на виду).
export function spinSpeed(viewLon, focusLon, base = 6) {
  const d = Math.abs(lonDelta(viewLon, focusLon))
  return d < 70 ? base * 0.35 : base * 1.6
}

// Ближайший объект под курсором (экранные точки {x, y, item}); null — ничего рядом.
export function pick(points, x, y, radius = 12) {
  let best = null
  let bestD = radius
  for (const p of points) {
    const d = Math.hypot(p.x - x, p.y - y)
    if (d <= bestD) { best = p; bestD = d }
  }
  return best
}

// Положение машины на дуге по плановому прогрессу (null — без сроков: «бежит» по кругу).
export function truckIndex(progress, steps, phase) {
  if (progress === null || progress === undefined) return Math.round(phase * steps) % (steps + 1)
  return Math.round(Math.max(0, Math.min(1, progress)) * steps)
}
